import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { TouchableOpacity, View, StyleSheet, Keyboard, } from 'react-native';
import Alert from 'utils/packages/Alert';
import getSymbolFromCurrency from 'currency-symbol-map';
import { AppView, Button, CardView, CollapsableSettingsSection, rem, SCROLL_INDICATOR_INSETS, SettingsItemDescription, SettingsItemInput, SettingsItemMoneyInput, SettingsItemNavigate, Spacer, StatusBar, withAnimatedNavigationHeader, } from 'design-system';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { useAppSelector } from 'store/hooks';
import { selectDefaultCurrency } from 'reducers/selectors';
import { IconSmartRule, IconBoldArrow, IconBoldCalendar, } from 'design-system/icons';
import IconTooltip from 'design-system/icons/IconTooltip';
import useColors from 'hooks/useColors';
import InfoCard from 'design-system/InfoCard';
import Text from 'design-system/Text';
import links from 'utils/links';
import { smartRuleTypeLabels } from 'utils/types/navigation';
import Modal from 'utils/packages/Modal';
import SelectDateRangeModalContent from 'features/subscriptions/components/modal/SelectDateRangeModalContent';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { IconLetters } from 'design-system/icons/IconLetters';
import { muteErrorAndFetching, useFetchOld, useKeyboardAdjustPan } from 'hooks';
import { getFilterPreview } from 'actions';
import { AnimatedKeyboardAwareScrollView } from 'design-system/AnimatedComponents';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import { SmartRuleIconForTransactionType } from '../components/SmartRuleIconForTransactionType';
import { SmartRuleDetectedTransactions } from '../components/SmartRuleDetectedTransactions';
import { SmartRuleTransactionTypeModal } from '../components/SmartRuleTransactionTypeModal';
import { getPeriodText } from '../utils';
import { isAndroid, isWeb } from '../../../constants';
const strings = {
    title: 'Let’s setup the smart rule filters',
    subtitle: 'These details will help detecting the transfers and trigger the rule',
};
const extraHeight = isAndroid ? rem(16) : rem(116);
const SmartRuleFiltersScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    useKeyboardAdjustPan();
    const currency = useAppSelector(selectDefaultCurrency);
    const currencySymbol = useMemo(() => getSymbolFromCurrency(currency), [currency]);
    const referenceRef = useRef(null);
    const minAmountRef = useRef(null);
    const maxAmountRef = useRef(null);
    const [filters, setFilters] = useState({
        accountIds: route.params.selectedAccounts,
        counterpartRegex: '',
        minAmount: undefined,
        maxAmount: undefined,
        minDayOfMonth: 1,
        maxDayOfMonth: 31,
        type: 'both',
    });
    useAutoFocusInput(referenceRef);
    const handleChangeFilters = useCallback((key, value) => {
        setFilters((oldFilters) => ({ ...oldFilters, [key]: value }));
    }, []);
    const colors = useColors();
    const handleLearnMore = useCallback(() => links.openSmartRules(), []);
    const [initialTransactions, , , fetchInitialTxns] = useFetchOld(muteErrorAndFetching);
    useEffect(() => {
        fetchInitialTxns(getFilterPreview({ accountIds: `[${filters.accountIds.join(',')}]` }, 1));
    }, [fetchInitialTxns, filters.accountIds]);
    const handlePickTransaction = useCallback(() => {
        navigation.navigateDeprecated('SmartRuleSelectTransaction', {
            initialTransactions: initialTransactions?.transactions,
            selectedAccounts: filters.accountIds,
            onSelect: (v) => handleChangeFilters('counterpartRegex', v),
        });
    }, [filters.accountIds, handleChangeFilters, initialTransactions]);
    const [forceOpen, forceOpenFilters] = useState({});
    const handleContinue = useCallback(() => {
        Keyboard.dismiss();
        const { counterpartRegex, minAmount, maxAmount } = filters;
        if (!counterpartRegex) {
            Alert.alert('Hold on 💫', 'You need to set a reference.', [
                { text: 'OK', onPress: () => referenceRef.current?.focus() },
            ]);
            return;
        }
        if (minAmount && maxAmount && minAmount > maxAmount) {
            Alert.alert('Hold on 💫', 'Minimum amount should be less than maximum.', [
                {
                    text: 'OK',
                    onPress: () => {
                        forceOpenFilters({});
                        minAmountRef.current?.focus();
                    },
                },
            ]);
            return;
        }
        navigation.navigateDeprecated('SmartRulePickOfflineAccount', {
            ...filters,
            name: route.params.name,
        });
    }, [filters, route.params.name]);
    const handlePickType = useCallback(() => {
        Keyboard.dismiss();
        SmartRuleTransactionTypeModal.show({
            onSelect: (type) => handleChangeFilters('type', type),
            selected: filters.type,
        });
    }, [filters.type, handleChangeFilters]);
    const handlePickPeriod = useCallback(() => {
        Keyboard.dismiss();
        Modal.show(<SelectDateRangeModalContent data={{
                start: filters.minDayOfMonth || undefined,
                end: filters.maxDayOfMonth || undefined,
            }} subtitle="" onFinish={(range) => {
                setFilters((oldFilters) => ({
                    ...oldFilters,
                    minDayOfMonth: range.start,
                    maxDayOfMonth: range.end,
                }));
                Modal.hide();
            }}/>, { title: isWeb ? 'Select a range' : undefined });
    }, [filters.maxDayOfMonth, filters.minDayOfMonth]);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const period = useMemo(() => getPeriodText(filters.minDayOfMonth, filters.maxDayOfMonth), [filters.maxDayOfMonth, filters.minDayOfMonth]);
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <AnimatedKeyboardAwareScrollView onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={paddingBottom} keyboardShouldPersistTaps="handled" enableResetScrollToCoords={false} enableOnAndroid extraHeight={extraHeight}>
          <ScreenInfoHeader title={strings.title} subTitle={strings.subtitle}/>
          <SmartRuleDetectedTransactions filters={filters}/>
          <CardView noMarginBottom noMarginTop>
            <SettingsItemInput ref={referenceRef} description={<View style={styles.referenceContainer}>
                  <SettingsItemDescription>Reference</SettingsItemDescription>
                  <TouchableOpacity onPress={() => navigation.navigateDeprecated('SmartRuleReferenceInfo')}>
                    <IconTooltip width={16} height={16} color={colors.text.secondary}/>
                  </TouchableOpacity>
                </View>} subDescription={<>
                  <Spacer h={4}/>
                  <TouchableOpacity onPress={handlePickTransaction} style={styles.referenceContainer}>
                    <IconSmartRule gradientKey="ultimate" width={16} height={16}/>
                    <Text TextThin-14 Primary>
                      Pick from transactions
                    </Text>
                  </TouchableOpacity>
                </>} icon={<IconLetters gradientKey="darkPurple"/>} value={filters.counterpartRegex} onChangeText={(v) => handleChangeFilters('counterpartRegex', v)} placeholder="Type..." iconSize={44} returnKeyType="done"/>
          </CardView>
          <CollapsableSettingsSection title="Advanced Filters" collapsed forceOpen={forceOpen} style={styles.collapsible}>
            <SettingsItemMoneyInput ref={minAmountRef} icon={<IconBoldArrow direction="left"/>} value={filters.minAmount ? filters.minAmount.toString() : ''} onClearTextInput={() => handleChangeFilters('minAmount', 0)} underlineColorAndroid="transparent" onChangeText={(_, v) => handleChangeFilters('minAmount', v === undefined ? undefined : Number(v))} description="Minimum amount (optional)" placeholder={`Set\xa0value...`} returnKeyLabel="Done" returnKeyType="done" prefix={currencySymbol} maximumFractionalDigits={0} placeholderTextColor={colors.text.placeholder}/>
            <SettingsItemMoneyInput ref={maxAmountRef} icon={<IconBoldArrow direction="right" gradientKey="skyBlue"/>} onClearTextInput={() => handleChangeFilters('maxAmount', 0)} value={filters.maxAmount ? filters.maxAmount.toString() : ''} underlineColorAndroid="transparent" onChangeText={(_, v) => handleChangeFilters('maxAmount', v === undefined ? undefined : Number(v))} description="Maximum amount (optional)" placeholder={`Set\xa0value...`} returnKeyLabel="Done" returnKeyType="done" prefix={currencySymbol} maximumFractionalDigits={0} placeholderTextColor={colors.text.placeholder}/>
            <SettingsItemNavigate title={smartRuleTypeLabels[filters.type]} icon={<SmartRuleIconForTransactionType type={filters.type}/>} onPress={handlePickType} description="Transaction type"/>
            <SettingsItemNavigate title={<Text Text-16>{period}</Text>} icon={<IconBoldCalendar />} description="Period" onPress={handlePickPeriod}/>
          </CollapsableSettingsSection>
          <InfoCard onPress={handleLearnMore} style={styles.infoCard}>
            <Text TextThin-14 Secondary>
              Need to understand filters better?
            </Text>
            <Text Text-14 Primary>
              Learn more
            </Text>
          </InfoCard>
        </AnimatedKeyboardAwareScrollView>

        <Button floating brand title="Continue" onPress={handleContinue}/>
      </AppView>);
}, ({ colors }) => ({
    title: strings.title,
    hideTitleOnScroll: true,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    referenceContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
        gap: rem(8),
    },
    infoCard: {
        marginTop: rem(16),
    },
    collapsible: {
        marginTop: rem(16),
    },
});
export default SmartRuleFiltersScreen;
